import React from 'react';
import { classifyTags } from '../../../utils/tagHelpers';
import { FLAG_MAP } from '../../../utils/flags';
import Tag from '../atoms/Tag';

export type TagContext = 'home' | 'search' | 'detail';
type Category =
  | 'diet'
  | 'spice'
  | 'cost'
  | 'cuisine'
  | 'nutrition'
  | 'meal'
  | 'occasion'
  | 'season';

interface RecipeTagListProps {
  /** either a plain list of names… */
  tags?: string[];
  /** …or your recipeTags array with nested `.tag.name` */
  recipeTags?: Array<{ id: string; name?: string; tag?: { name: string } }>;
  context?: TagContext;
}

const MAX_TAGS: Record<TagContext, number> = {
  home: 2,
  search: 1,
  detail: 3,
};

export default function RecipeTagList({
  tags,
  recipeTags,
  context = 'detail',
}: RecipeTagListProps) {
  // 1) flatten into string[] and lowercase
  const raw: unknown[] =
    tags ??
    recipeTags?.map((rt) => {
      if (typeof rt === 'string') return rt;
      if (typeof rt.name === 'string') return rt.name;
      if (rt.tag && typeof rt.tag.name === 'string') return rt.tag.name;
      return undefined;
    }) ??
    [];

  const tagNames = raw.filter((x): x is string => typeof x === 'string')
                      .map((s) => s.toLowerCase());

  // 2) classify
  const { dietary, spicy, cost, nutrition, mealType, occasion, season } =
    classifyTags(tagNames);

  // 3) build in priority order
  const out: { name: string; category: Category }[] = [];

  // 1) Diet
  if (dietary[0]) out.push({ name: dietary[0], category: 'diet' });

  // 2) Spice (if > none)
  if (spicy && spicy > 1) {
    const names = ['none', 'mild', 'medium', 'hot'];
    out.push({ name: names[spicy - 1], category: 'spice' });
  }

  // 3) Cost (only cheap)
  if (cost === 1) out.push({ name: 'cheap', category: 'cost' });

  // 4) Cuisine: pick the first tag that’s a country or region
  const cuisine = tagNames.find((t) =>
    // country?
    FLAG_MAP.hasOwnProperty(t) ||
    // known regions (emoji fallback)
    ['south_american','african','middle_eastern','mediterranean'].includes(t)
  );
  if (cuisine) out.push({ name: cuisine, category: 'cuisine' });

  // 5) Nutrition (up to 2, detail only)
  if (context === 'detail') {
    nutrition.slice(0, 2).forEach((n) =>
      out.push({ name: n, category: 'nutrition' })
    );
  }

  // 6) Meal → Occasion → Season
  out.push(
    ...mealType.map((m) => ({ name: m, category: 'meal' as Category })),
    ...occasion.map((o) => ({ name: o, category: 'occasion' as Category })),
    ...season.map((s) => ({ name: s, category: 'season' as Category }))
  );

  // 4) now clamp to max
  const finalTags = out.slice(0, MAX_TAGS[context]);

  return (
    <div className="flex flex-wrap gap-2">
      {finalTags.map((t) => (
        <Tag
          key={t.category + t.name}
          name={t.name}
          category={t.category}
          variant="pill"
        />
      ))}
    </div>
  );
}
